import { useMemo } from 'react';
import { buScopeParams } from '@/lib/business-unit-scope';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { responseStrategyTemplateService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import type { UpdateResponseStrategyTemplateDto } from '@/modules/response-strategies/types';

const responseStrategyTemplatesQueryAtom = atomWithQuery(() => ({
	queryKey: ['response-strategy-templates'],
	queryFn: responseStrategyTemplateService.getTemplates
}));

const responseStrategyTemplateByIdQueryAtom = (id: string) =>
	atomWithQuery(() => ({
		queryKey: ['response-strategy-templates', id],
		queryFn: () => responseStrategyTemplateService.getTemplateById(id),
		enabled: !!id
	}));

const updateResponseStrategyTemplateMutationAtom = atomWithMutation(() => ({
	mutationKey: ['response-strategy-templates', 'update'],
	mutationFn: ({ id, data }: { id: string; data: UpdateResponseStrategyTemplateDto }) =>
		responseStrategyTemplateService.updateTemplate(id, data),
	onSuccess: (_data, variables) => {
		invalidateQueries(['response-strategy-templates']);
		invalidateQueries(['response-strategy-templates', variables.id]);
	}
}));

const deleteResponseStrategyTemplateMutationAtom = atomWithMutation(() => ({
	mutationKey: ['response-strategy-templates', 'delete'],
	mutationFn: (id: string) => responseStrategyTemplateService.deleteTemplate(id),
	onSuccess: () => {
		invalidateQueries(['response-strategy-templates']);
	}
}));

const updateResponseStrategyTemplateEditableFieldsMutationAtom = atomWithMutation((get) => ({
	mutationKey: ['response-strategy-templates', 'update-editable-fields'],
	mutationFn: ({ id, data }: { id: string; data: { intents?: string[]; isActive?: boolean } }) =>
		responseStrategyTemplateService.updateTemplateEditableFields(id, data, buScopeParams(get)),
	onSuccess: (_data, variables) => {
		invalidateQueries(['response-strategy-templates']);
		invalidateQueries(['response-strategy-templates', variables.id]);
		// This PATCH actually upserts into response_strategies on the backend
		// (an account-scoped override), not the shared template doc — without
		// this, the strategies list stays stale and the toggle looks like it
		// didn't take until a full page reload.
		invalidateQueries(['response-strategies']);
	}
}));

export function useResponseStrategyTemplates() {
	const [query] = useAtom(responseStrategyTemplatesQueryAtom);
	return query;
}

export function useResponseStrategyTemplate(id: string) {
	const atom = useMemo(() => responseStrategyTemplateByIdQueryAtom(id), [id]);
	const [query] = useAtom(atom);
	return query;
}

export function useUpdateResponseStrategyTemplate() {
	const [mutation] = useAtom(updateResponseStrategyTemplateMutationAtom);
	return mutation;
}

export function useDeleteResponseStrategyTemplate() {
	const [mutation] = useAtom(deleteResponseStrategyTemplateMutationAtom);
	return mutation;
}

export function useUpdateResponseStrategyTemplateEditableFields() {
	const [mutation] = useAtom(updateResponseStrategyTemplateEditableFieldsMutationAtom);
	return mutation;
}
